import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import { SitePadding, SiteWidth } from '@helpers/grid';
import { Device } from '@helpers/media';
import { parseToHsl } from 'polished';
import { createGlobalStyle, css } from 'styled-components';
import resetCss from '../../../../client/reset.css?raw';
/* Font imports */
import cabin from './cabin';
import lato from './lato';
import notoSans from './noto-sans';
import poppins from './poppins';
import roboto from './roboto';
import sourceSerif from './source-serif';
import workSans from './work-sans';
export var GlobalFontsModel;
(function (GlobalFontsModel) {
    GlobalFontsModel["default"] = "Cabin";
    GlobalFontsModel["area"] = "Area";
    GlobalFontsModel["bookmania"] = "Bookmania";
    GlobalFontsModel["lato"] = "Lato";
    GlobalFontsModel["nimbusSans"] = "Numbus Sans";
    GlobalFontsModel["notoSans"] = "Noto Sans";
    GlobalFontsModel["poppins"] = "Poppins";
    GlobalFontsModel["roboto"] = "Roboto";
    GlobalFontsModel["sourceSerif"] = "Source Serif";
    GlobalFontsModel["workSans"] = "Work Sans";
})(GlobalFontsModel || (GlobalFontsModel = {}));
const DEFAULT = {
    accentPrimary: brand.accent.primary,
    accentSecondary: brand.accent.secondary,
    background: brand.global.white,
    highlightPrimary: brand.highlight.primary,
    highlightSecondary: brand.highlight.secondary,
    hyperlink: brand.accent.primary,
    invalid: brand.validation.invalid,
    primary: brand.global.black,
    primaryAlt: brand.accent.secondary,
    valid: brand.validation.valid,
    topLinkText: brand.highlight.topLink,
    menuHover: brand.highlight.menu,
};
/**
 * creates two variables from a hex code, the colour variable and a string version
 * which can be used anywhere a colour needs opacity applying
 *
 * eg. hsl(var(--colourVarString) / 0.5))
 */
const toHslVars = (hex, name) => {
    const hsl = parseToHsl(hex);
    const hslString = `${hsl.hue} ${hsl.saturation * 100}% ${hsl.lightness * 100}%`;
    return `--${name}: hsl(${hslString}); --${name}String: ${hslString};`;
};
const handleFontDefinition = (font) => {
    switch (font) {
        case GlobalFontsModel.area:
            return css `
        --fontFamily: 'area-normal', sans-serif;

        font-family: 'area-normal', sans-serif;
        font-weight: 400;
        font-style: normal;
        font-display: swap;
      `;
        case GlobalFontsModel.bookmania:
            return css `
        --fontFamily: 'bookmania', serif;

        font-family: 'bookmania', serif;
        font-weight: 400;
        font-style: normal;
        font-display: swap;
      `;
        case GlobalFontsModel.lato:
            return css `
        ${lato};

        --fontFamily: 'Lato', sans-serif;

        font-family: 'Lato', sans-serif;
        font-weight: 400;
        font-style: normal;
        font-display: swap;
      `;
        case GlobalFontsModel.nimbusSans:
            return css `
        --fontFamily: 'nimbus-sans', sans-serif;

        font-family: 'nimbus-sans', sans-serif;
        font-weight: 400;
        font-style: normal;
        font-display: swap;
      `;
        case GlobalFontsModel.notoSans:
            return css `
        ${notoSans};

        --fontFamily: 'NotoSans', sans-serif;

        font-family: 'NotoSans', sans-serif;
        font-weight: 400;
        font-style: normal;
        font-display: swap;
      `;
        case GlobalFontsModel.poppins:
            return css `
        ${poppins};

        --fontFamily: 'Poppins', sans-serif;

        font-family: 'Poppins', sans-serif;
        font-weight: 400;
        font-style: normal;
        font-display: swap;
      `;
        case GlobalFontsModel.roboto:
            return css `
        ${roboto};

        --fontFamily: 'Roboto', sans-serif;

        font-family: 'Roboto', sans-serif;
        font-weight: 400;
        font-style: normal;
        font-display: swap;
      `;
        case GlobalFontsModel.sourceSerif:
            return css `
        ${sourceSerif};

        --fontFamily: 'SourceSerif', serif;

        font-family: 'SourceSerif', serif;
        font-weight: 400;
        font-style: normal;
        font-display: swap;
      `;
        case GlobalFontsModel.workSans:
            return css `
        ${workSans};

        --fontFamily: 'WorkSans', sans-serif;

        font-family: 'WorkSans', sans-serif;
        font-weight: 400;
        font-style: normal;
        font-display: swap;
      `;
        case GlobalFontsModel.default:
        default:
            return css `
        ${cabin};

        --fontFamily: 'Cabin', sans-serif;

        font-family: 'Cabin', sans-serif;
        font-weight: 400;
        font-style: normal;
        font-display: swap;
      `;
    }
};
const GlobalStyles = createGlobalStyle `
  ${resetCss};

  :root {
    ${({ theme }) => theme
    ? css `
            /* Font family definitions */
            ${handleFontDefinition(theme.font)}

            /* Colour var definitions */
            ${toHslVars(theme.accentPrimary, 'accentColorPrimary')}
            ${toHslVars(theme.accentSecondary, 'accentColorSecondary')}
            ${toHslVars(theme.highlightPrimary, 'highlightColorPrimary')}
            ${toHslVars(theme.highlightSecondary, 'highlightColorSecondary')}
            ${toHslVars(theme.hyperlink, 'hyperlink')}
            ${toHslVars(theme.background, 'bgColor')}
            ${toHslVars(theme.primary, 'fgColor')}
            ${toHslVars(theme.primaryAlt, 'primaryAltColor')}
            ${toHslVars(theme.menuHover, 'menuHoverColor')}
            ${toHslVars(theme.topLinkText, 'topLinkText')}
            ${toHslVars(theme.valid, 'validColor')}
            ${toHslVars(theme.invalid, 'invalidColor')}
          `
    : css `
            /* Font family definitions */
            ${handleFontDefinition(GlobalFontsModel.default)}

            /* Colour var definitions */
            ${toHslVars(DEFAULT.accentPrimary, 'accentColorPrimary')}
            ${toHslVars(DEFAULT.accentSecondary, 'accentColorSecondary')}
            ${toHslVars(DEFAULT.highlightPrimary, 'highlightColorPrimary')}
            ${toHslVars(DEFAULT.highlightSecondary, 'highlightColorSecondary')}
            ${toHslVars(DEFAULT.hyperlink, 'hyperlink')}
            ${toHslVars(DEFAULT.background, 'bgColor')}
            ${toHslVars(DEFAULT.primary, 'fgColor')}
            ${toHslVars(DEFAULT.primaryAlt, 'primaryAltColor')}
            ${toHslVars(DEFAULT.menuHover, 'menuHoverColor')}
            ${toHslVars(DEFAULT.topLinkText, 'topLinkText')}
            ${toHslVars(DEFAULT.valid, 'validColor')}
            ${toHslVars(DEFAULT.invalid, 'invalidColor')}
          `}

    --borderRadius: 6px;
    --gutterWidth: ${fluid(16, 32, Device.Tablet)};
    --headerHeight: initial;
    --scrollMargin: calc(var(--headerHeight) + 16px);
    --sectionSpacing: ${fluid(34, 68)};
    --sitePadding: ${SitePadding}px;
    --siteWidth: ${SiteWidth}px;

    background-color: var(--bgColor);
  }

  html {
    height: 100%;
    scroll-behavior: smooth;
    padding-top: var(--headerHeight);
  }

  html > .sb-show-main, 
  body {
    background: var(--bgColor) !important;
  }

  body {
    color: var(--fgColor);
    overscroll-behavior-x: none;
    height: 100%;
    /** 
    Ensure that main can flex to fill entire space between
    header and footer even when it would naturally be smaller 
    */
    display: flex;
    flex-direction: column;
  }

  main {
    /** 
    Ensure that main div fills entire space between
    header and footer at all times
    */
    flex: 1;
  }

  img {
    max-width: 100%;
  }

  * {
    box-sizing: border-box;
  }

  span {
    display: inline-block;
  }

  sup {
    position: relative;
    top: -0.5em;
    font-size: 80%;
  }

  sub {
    position: relative;
    top: 0.4em;
    font-size: 80%;
  }

  
  input, select {
    font-family: var(--fontFamily);
  }

  .hidden {
    display: none;
    visibility: none;
  }
`;
export default GlobalStyles;
